/**
 * Quote engine — the single source of truth for what a migration costs.
 *
 * Pure and dependency-free (imports only scan TYPES) so the marketing
 * estimator, the in-app estimator, and the Stripe checkout action all price
 * identically. All money is carried in integer CENTS to avoid float drift; the
 * UI formats to dollars.
 *
 * LOCKED RATES (tunable in one place — `RATES` below):
 *   base $100 = first 1,000 messages
 *   + $15 per additional 1,000 messages
 *   + pictures $30 / GB
 *   + video    $75 / GB
 *   + files    $10 / GB
 *   floor $100.   Free PoC = 1 channel, last 100 messages = $0.
 */
import { UPLOAD_THRESHOLDS } from "./scan";
import type { ChannelScan, ContentType, OverLimit, TimeBucket } from "./scan";

export const RATES = {
  /** $100 base covers the first `baseMessages` messages. */
  baseCents: 10_000,
  baseMessages: 1_000,
  /** $15 per additional 1,000 messages, charged per whole block. */
  perBlockCents: 1_500,
  blockMessages: 1_000,
  /** Per-GB media rates. */
  picsCentsPerGB: 3_000,
  videoCentsPerGB: 7_500,
  filesCentsPerGB: 1_000,
  /** No paid job is below $100. */
  floorCents: 10_000,
  /** Decimal GB (marketing/storage convention). */
  bytesPerGB: 1_000_000_000,
  /** Free proof-of-concept: one channel, up to this many recent messages. */
  freePoc: { maxChannels: 1, maxMessages: 100 },
  /**
   * Hosted-media fee: $1.00 per GB per MONTH (recurring, opt-in). For files over
   * the customer's Discord upload limit that they choose us to host + link. Our
   * R2 cost is ~$0.015/GB, so this is intentional margin and a nudge toward
   * boosting the server for real volume.
   */
  hostedMediaCentsPerGBMonth: 100,
  /**
   * Approx flat monthly cost to reach a Discord boost level (paid to Discord,
   * shown for comparison — we don't bill it). Level 2 ≈ 7 boosts, Level 3 ≈ 14,
   * at ~$4.99/boost.
   */
  boostMonthlyCents: { 2: 3_500, 3: 7_000 },
} as const;

/** Content types the customer toggles on/off. */
export interface ContentSelection {
  text: boolean;
  pics: boolean;
  video: boolean;
  files: boolean;
}

/**
 * Time scope of the migration. `window` maps directly to a nested scan bucket;
 * `lastN` prices the most-recent N messages (media scaled proportionally, since
 * the scan buckets media by time, not by message index).
 */
export type TimeRange =
  | { kind: "all" }
  | { kind: "window"; bucket: "last1y" | "last6mo" | "last90d" }
  | { kind: "lastN"; messages: number };

export interface Selection {
  channelIds: string[];
  contentTypes: ContentSelection;
  timeRange: TimeRange;
}

// ── Discord upload caps (boost-level aware) ──────────────────────────────────

/** Discord server boost level. 0/1 = 10 MB, 2 = 50 MB, 3 = 100 MB per file. */
export type BoostTier = 0 | 1 | 2 | 3;

/** Per-file upload cap (bytes) at a boost tier. */
export function tierLimitBytes(tier: BoostTier): number {
  if (tier >= 3) return UPLOAD_THRESHOLDS.t100;
  if (tier === 2) return UPLOAD_THRESHOLDS.t50;
  return UPLOAD_THRESHOLDS.t10;
}

/** The over-cap bucket whose files are skipped at a boost tier. */
function skippedKey(tier: BoostTier): keyof OverLimit {
  if (tier >= 3) return "over100";
  if (tier === 2) return "over50";
  return "over10";
}

/** Boosting to which tier next reduces skips (null if already maxed). */
export function nextBoostTier(tier: BoostTier): BoostTier | null {
  if (tier <= 1) return 2;
  if (tier === 2) return 3;
  return null;
}

export interface UploadSkip {
  tier: BoostTier;
  limitBytes: number;
  /** Files (and bytes) that exceed the cap and would be SKIPPED at this tier. */
  count: number;
  bytes: number;
  /** How many of those a boost would rescue, and to which level. */
  includableByBoost: { toTier: BoostTier; count: number; bytes: number } | null;
}

/** Sum over-cap media across the selected channels for a given boost tier. */
function skippedAt(
  channels: ChannelScan[],
  channelIds: string[],
  tier: BoostTier,
): { count: number; bytes: number } {
  const key = skippedKey(tier);
  let count = 0;
  let bytes = 0;
  for (const c of channels) {
    if (!channelIds.includes(c.id) || !c.overLimit) continue;
    for (const type of ["pics", "video", "files"] as const) {
      count += c.overLimit[type][key].count;
      bytes += c.overLimit[type][key].bytes;
    }
  }
  return { count, bytes };
}

/**
 * What the customer's current Discord server would SKIP (files over its upload
 * cap) for the selected channels, and how much a boost would rescue. Drives the
 * honest upload-limit disclosure in the estimator.
 */
export function uploadSkip(
  channels: ChannelScan[],
  channelIds: string[],
  tier: BoostTier,
): UploadSkip {
  const here = skippedAt(channels, channelIds, tier);
  const target = nextBoostTier(tier);
  let includableByBoost: UploadSkip["includableByBoost"] = null;
  if (target !== null) {
    const after = skippedAt(channels, channelIds, target);
    includableByBoost = {
      toTier: target,
      count: here.count - after.count,
      bytes: here.bytes - after.bytes,
    };
  }
  return {
    tier,
    limitBytes: tierLimitBytes(tier),
    count: here.count,
    bytes: here.bytes,
    includableByBoost,
  };
}

// ── Oversized-media hosting economics (the 3-option chooser) ─────────────────

/** Monthly $1/GB hosting fee for `bytes`, rounded to the cent. */
export function hostingCostCents(bytes: number): number {
  if (bytes <= 0) return 0;
  return Math.round((bytes / RATES.bytesPerGB) * RATES.hostedMediaCentsPerGBMonth);
}

export interface BoostOption {
  toTier: BoostTier;
  /** Approx flat monthly Discord cost (paid to Discord, not us). */
  monthlyCents: number;
  capBytes: number;
  /** Files (and bytes) this boost rescues — plays them inline natively. */
  rescuedCount: number;
  rescuedBytes: number;
}

export interface MediaOptions {
  tier: BoostTier;
  currentCapBytes: number;
  /** All media over the current cap (skipped today unless boosted/hosted). */
  overLimit: { count: number; bytes: number };
  /** Subset over 100 MB — can't native-upload at ANY boost: host-or-skip only. */
  mustHost: { count: number; bytes: number };
  /** $1/GB/mo to host everything over the cap. */
  hostAllMonthlyCents: number;
  /** $1/GB/mo to host just the unavoidable >100 MB files. */
  mustHostMonthlyCents: number;
  /** Boost levels above the current one that rescue >0 bytes. */
  boosts: BoostOption[];
  /** GB of over-limit media at which the cheapest boost equals hosting cost. */
  crossoverGB: number | null;
  /** True once hosting all over-limit media costs more than the cheapest boost. */
  boostCheaperThanHosting: boolean;
}

/**
 * The economics of the three ways to handle media that exceeds the connected
 * server's Discord upload cap: Boost (flat, plays inline + raises the team's
 * limit), Host ($1/GB/mo, and the ONLY option for >100 MB files), or Skip ($0).
 * Pure — same inputs → same numbers on the client and the server.
 */
export function mediaOptions(
  channels: ChannelScan[],
  channelIds: string[],
  tier: BoostTier,
): MediaOptions {
  const here = skippedAt(channels, channelIds, tier);
  const overMax = skippedAt(channels, channelIds, 3); // > 100 MB

  const boosts: BoostOption[] = [];
  for (const target of [2, 3] as const) {
    if (target <= tier) continue;
    const after = skippedAt(channels, channelIds, target);
    const rescuedBytes = here.bytes - after.bytes;
    const rescuedCount = here.count - after.count;
    if (rescuedBytes > 0) {
      boosts.push({
        toTier: target,
        monthlyCents: RATES.boostMonthlyCents[target],
        capBytes: tierLimitBytes(target),
        rescuedCount,
        rescuedBytes,
      });
    }
  }

  const cheapestBoost = boosts.length
    ? Math.min(...boosts.map((b) => b.monthlyCents))
    : null;
  const crossoverGB =
    cheapestBoost !== null ? cheapestBoost / RATES.hostedMediaCentsPerGBMonth : null;
  const overGB = here.bytes / RATES.bytesPerGB;

  return {
    tier,
    currentCapBytes: tierLimitBytes(tier),
    overLimit: here,
    mustHost: overMax,
    hostAllMonthlyCents: hostingCostCents(here.bytes),
    mustHostMonthlyCents: hostingCostCents(overMax.bytes),
    boosts,
    crossoverGB,
    boostCheaperThanHosting: crossoverGB !== null && overGB > crossoverGB,
  };
}

export interface QuoteLineItem {
  key: "messages" | "pics" | "video" | "files";
  label: string;
  /** Human detail, e.g. "12,480 messages" or "4.20 GB". */
  detail: string;
  amountCents: number;
}

export interface Quote {
  isFree: boolean;
  /** Priced message count and media bytes after time-range scoping. */
  messages: number;
  picsBytes: number;
  videoBytes: number;
  filesBytes: number;
  lineItems: QuoteLineItem[];
  subtotalCents: number;
  floorApplied: boolean;
  amountCents: number;
}

/** The scan bucket a time range reads from. `lastN`/`all` read all-time. */
function bucketForRange(range: TimeRange): TimeBucket {
  return range.kind === "window" ? range.bucket : "allTime";
}

/** Message cost: $100 base (incl. first 1,000) + $15 per additional 1,000 block. */
export function messageCostCents(messages: number): number {
  if (messages <= 0) return 0;
  const extra = Math.max(0, messages - RATES.baseMessages);
  const blocks = Math.ceil(extra / RATES.blockMessages);
  return RATES.baseCents + blocks * RATES.perBlockCents;
}

/** Pro-rated media cost for `bytes` at `centsPerGB`, rounded to the cent. */
export function mediaCostCents(bytes: number, centsPerGB: number): number {
  if (bytes <= 0) return 0;
  return Math.round((bytes / RATES.bytesPerGB) * centsPerGB);
}

function gbLabel(bytes: number): string {
  return `${(bytes / RATES.bytesPerGB).toFixed(2)} GB`;
}

/**
 * Media bytes of `type` in `channel` that will actually migrate. When a Discord
 * boost `tier` is supplied, files above that tier's upload cap are SKIPPED (not
 * uploaded, not charged); the all-time over-cap total is pro-rated onto the
 * selected time bucket.
 */
function includedMediaBytes(
  channel: ChannelScan,
  type: "pics" | "video" | "files",
  bucket: TimeBucket,
  tier: BoostTier | undefined,
): number {
  const bytes = channel[type][bucket].bytes;
  if (tier === undefined || !channel.overLimit) return bytes;
  const allTime = channel[type].allTime.bytes;
  const over = channel.overLimit[type][skippedKey(tier)].bytes;
  if (allTime <= 0 || over <= 0) return bytes;
  const excluded = over * (bytes / allTime);
  return Math.max(0, bytes - excluded);
}

/**
 * Price the selected subset of a scan. Pure: same inputs → same quote, on the
 * client and on the server. Pass `uploadLimitTier` to exclude media that would
 * be skipped at the connected server's boost level, so the quote reflects only
 * what actually migrates.
 */
export function computeQuote(
  selection: Selection,
  channels: ChannelScan[],
  opts?: { uploadLimitTier?: BoostTier },
): Quote {
  const { channelIds, contentTypes, timeRange } = selection;
  const chosen = channels.filter((c) => channelIds.includes(c.id));
  const bucket = bucketForRange(timeRange);
  const tier = opts?.uploadLimitTier;

  const wants = (t: ContentType) => contentTypes[t];

  let messages = 0;
  let picsBytes = 0;
  let videoBytes = 0;
  let filesBytes = 0;
  for (const c of chosen) {
    if (wants("text")) messages += c.text[bucket].count;
    if (wants("pics")) picsBytes += includedMediaBytes(c, "pics", bucket, tier);
    if (wants("video")) videoBytes += includedMediaBytes(c, "video", bucket, tier);
    if (wants("files")) filesBytes += includedMediaBytes(c, "files", bucket, tier);
  }

  // "Last N messages": clamp messages and scale media by the same fraction, so a
  // partial-history migration is priced fairly against a time-bucketed scan.
  if (timeRange.kind === "lastN") {
    const n = Math.max(0, Math.floor(timeRange.messages));
    const frac = messages > 0 ? Math.min(1, n / messages) : 0;
    messages = Math.min(messages, n);
    picsBytes = Math.round(picsBytes * frac);
    videoBytes = Math.round(videoBytes * frac);
    filesBytes = Math.round(filesBytes * frac);
  }

  // Free proof-of-concept: a single channel, last ≤100 messages, is on the house.
  const isFree =
    timeRange.kind === "lastN" &&
    timeRange.messages <= RATES.freePoc.maxMessages &&
    chosen.length <= RATES.freePoc.maxChannels;

  const lineItems: QuoteLineItem[] = [];
  const msgCost = wants("text") ? messageCostCents(messages) : 0;
  if (wants("text")) {
    lineItems.push({
      key: "messages",
      label: "Messages",
      detail: `${messages.toLocaleString("en-US")} messages`,
      amountCents: msgCost,
    });
  }
  if (wants("pics")) {
    lineItems.push({
      key: "pics",
      label: "Pictures",
      detail: gbLabel(picsBytes),
      amountCents: mediaCostCents(picsBytes, RATES.picsCentsPerGB),
    });
  }
  if (wants("video")) {
    lineItems.push({
      key: "video",
      label: "Video",
      detail: gbLabel(videoBytes),
      amountCents: mediaCostCents(videoBytes, RATES.videoCentsPerGB),
    });
  }
  if (wants("files")) {
    lineItems.push({
      key: "files",
      label: "Files",
      detail: gbLabel(filesBytes),
      amountCents: mediaCostCents(filesBytes, RATES.filesCentsPerGB),
    });
  }

  const subtotalCents = lineItems.reduce((s, li) => s + li.amountCents, 0);

  if (isFree) {
    return {
      isFree: true,
      messages,
      picsBytes,
      videoBytes,
      filesBytes,
      lineItems,
      subtotalCents,
      floorApplied: false,
      amountCents: 0,
    };
  }

  const floorApplied = subtotalCents < RATES.floorCents;
  const amountCents = Math.max(subtotalCents, RATES.floorCents);
  return {
    isFree: false,
    messages,
    picsBytes,
    videoBytes,
    filesBytes,
    lineItems,
    subtotalCents,
    floorApplied,
    amountCents,
  };
}

/** `1234567` → `"$12,345.67"`. */
export function formatUsd(cents: number): string {
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}
